import { evaluateCapacity } from "./capacityEngine.js";

const toDate = (value) => {
  const dt = value ? new Date(value) : null;
  return Number.isFinite(dt?.getTime?.()) ? dt : null;
};

const makeId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `alert-${Date.now()}-${Math.random().toString(16).slice(2)}`);

function isDependencySatisfied(task, allTasks) {
  const dep = allTasks.find((t) => t.stage === task.dependsOn || t.id === task.dependsOn);
  if (!dep) return true;
  return !!dep.completed;
}

/**
 * Derive planner alerts (overdue, due soon, blocked) and capacity pressure.
 * Backward compatible: accept either an array of tasks or { tasks, capacity }.
 * Pure derivation — no side effects or persistence.
 */
export function buildPlannerAlerts(input = {}, now = new Date()) {
  const tasks = Array.isArray(input) ? input : input.tasks || [];
  const capacity = Array.isArray(input) ? null : input.capacity || null;
  const alerts = [];
  const nowMs = now.getTime();

  (tasks || []).forEach((task) => {
    const dueDate = toDate(task.dueDate || task.end || task.deadline);
    const completed = !!task.completed;
    if (dueDate) {
      const daysDiff = (dueDate.getTime() - nowMs) / (1000 * 60 * 60 * 24);
      if (daysDiff < 0 && !completed) {
        alerts.push({
          id: makeId(),
          severity: "error",
          source: "planner",
          title: "Overdue task",
          message: `${task.title || "Task"} was due on ${dueDate.toLocaleDateString()}`,
          entityType: "task",
          entityId: task.id,
          createdAt: new Date().toISOString(),
          read: false,
        });
      } else if (daysDiff >= 0 && daysDiff <= 2 && !completed) {
        alerts.push({
          id: makeId(),
          severity: "warning",
          source: "planner",
          title: "Task due soon",
          message: `${task.title || "Task"} is due by ${dueDate.toLocaleDateString()}`,
          entityType: "task",
          entityId: task.id,
          createdAt: new Date().toISOString(),
          read: false,
        });
      }
    }

    if (task.dependsOn && !isDependencySatisfied(task, tasks)) {
      alerts.push({
        id: makeId(),
        severity: "warning",
        source: "planner",
        title: "Task blocked",
        message: `${task.title || "Task"} is waiting on a previous stage to complete.`,
        entityType: "task",
        entityId: task.id,
        createdAt: new Date().toISOString(),
        read: false,
      });
    }
  });

  if (capacity) {
    const { level, utilizationPct } = evaluateCapacity(capacity || {});
    if (level === "over" || level === "near" || level === "high") {
      const pct = Math.round((utilizationPct || 0) * 100);
      alerts.push({
        id: "capacity-pressure",
        severity: "warning",
        source: "planner",
        title: "Studio nearing capacity",
        message: `Current load ~${pct}%—consider rescheduling or reducing intake.`,
        entityType: "capacity",
        createdAt: new Date().toISOString(),
        read: false,
      });
    }
  }

  return alerts;
}
